import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Mic, Square, Upload, RefreshCcw, Check, ArrowRight, Server, Loader, X } from "lucide-react";

const ActionButton = ({ icon, label, onClick, disabled, variant = 'primary' }) => {
    const variants = {
        primary: 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:bg-slate-800',
        danger: 'bg-red-500 hover:bg-red-600',
        success: 'bg-green-600 hover:bg-green-700',
        play: 'bg-blue-600 hover:bg-blue-700'
    };
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={
                `flex items-center justify-center gap-2 sm:h-11 h-10 sm:px-4 px-3 w-full sm:w-auto rounded-lg text-sm sm:text-xs font-medium text-white shadow-sm hover:shadow-md  transition-all duration-200 disabled:bg-slate-400 disabled:cursor-not-allowed flex-shrink-0 sm:flex-shrink
            ${variants[variant]}`}>
            {icon}
            <span className=" text-xs font-semibold text-white tracking-wide uppercase">{label}</span> </button>);
};

const roboman_api = process.env.REACT_APP_ROBOMAN_API;
const UploadVoiceStep = ({ onUploadSuccess, onUploadError }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioFile, setAudioFile] = useState(null);
    const [status, setStatus] = useState("Begin by recording or uploading a file.");
    const [loading, setLoading] = useState(false);
    const [uploadData, setUploadData] = useState(null);
    const [isAudioReady, setIsAudioReady] = useState(false);
    const [isStartingOver, setIsStartingOver] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [hasExistingVoice, setHasExistingVoice] = useState(false); // ✅ NEW: State to track existing voice from profile

    const mediaRecorderRef = useRef(null);
    const audioRef = useRef(null);
    const chunks = useRef([]);

    // ✅ MODIFIED: On component mount, check for both user_data and session-specific uploads
    useEffect(() => {
        let initialStatusSet = false;

        // 1. Check for a pre-existing voice_note_id from the main user profile
        const userDataString = localStorage.getItem('user_data');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                if (userData?.user_info?.voice_note_id) {
                    setHasExistingVoice(true);
                    setStatus("A voice note is already on file for this user.");
                    initialStatusSet = true;
                }
            } catch (error) {
                console.error("Failed to parse user_data from localStorage", error);
            }
        }

        // 2. Check for a voice uploaded specifically within this component/session (takes precedence)
        const savedData = localStorage.getItem('voiceUploadResponse');
        if (savedData) {
            try {
                setUploadData(JSON.parse(savedData));
                setStatus("Loaded previously uploaded voice.");
                setHasExistingVoice(false); // A session-specific upload overrides the profile one
                initialStatusSet = true;
            } catch (error) {
                localStorage.removeItem('voiceUploadResponse');
            }
        }

        if (!initialStatusSet) {
            setStatus("Begin by recording or uploading a file.");
        }
    }, []);

    useEffect(() => {
        const audioEl = audioRef.current;
        if (!audioEl) return;
        const handleCanPlay = () => setIsAudioReady(true);
        const handleLoadStart = () => setIsAudioReady(false);
        audioEl.addEventListener('canplaythrough', handleCanPlay);
        audioEl.addEventListener('loadstart', handleLoadStart);
        audioEl.src = "";
        if (audioFile) {
            audioEl.src = URL.createObjectURL(audioFile);
            audioEl.load();
        } else if (uploadData?.url && !isStartingOver) {
            audioEl.src = uploadData.url;
            audioEl.load();
        }
        return () => {
            audioEl.removeEventListener('canplaythrough', handleCanPlay);
            audioEl.removeEventListener('loadstart', handleLoadStart);
        };
    }, [audioFile, uploadData, isStartingOver]);

    const startRecording = async () => {
        try { const stream = await navigator.mediaDevices.getUserMedia({ audio: true }); mediaRecorderRef.current = new MediaRecorder(stream); mediaRecorderRef.current.ondataavailable = (e) => chunks.current.push(e.data); mediaRecorderRef.current.onstop = () => { const blob = new Blob(chunks.current, { type: "audio/mpeg" }); chunks.current = []; setAudioFile(new File([blob], "recording.mp3", { type: "audio/mpeg" })); setStatus("Recording complete. Preview or upload."); }; mediaRecorderRef.current.start(); setIsRecording(true); setStatus("Recording..."); } catch (err) { setStatus("Mic access denied."); }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current) { mediaRecorderRef.current.stop(); mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop()); setIsRecording(false); }
    };

    const togglePlay = () => {
        if (!audioRef.current || !isAudioReady) return;
        isPlaying ? audioRef.current.pause() : audioRef.current.play();
        setIsPlaying(!isPlaying);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0]; if (file) { setAudioFile(file); setStatus(`File selected: ${file.name}`); }
    };

    const handleClearTemporary = () => {
        setAudioFile(null);
        setIsPlaying(false);
        setStatus("Begin by recording or uploading a file.");
    };

    const handleStartOver = () => {
        setIsStartingOver(true);
        setAudioFile(null);
        setStatus("Record a new voice note or upload a file.");
    }

    const handleCancelStartOver = () => {
        setIsStartingOver(false);
        setStatus("Loaded previously uploaded voice.");
    }

    // ✅ NEW: Handler to switch from the "voice exists" view to the recording view
    const handleChangeVoice = () => {
        setHasExistingVoice(false);
        setStatus("Record a new voice or upload a file.");
    };

    const handleUpload = async () => {
        if (!audioFile) return;
        setLoading(true);
        setStatus("Uploading...");

        try {
            const token = localStorage.getItem("access_token");
            if (!token) throw new Error("Authentication token not found.");
            const formData = new FormData();
            formData.append("uploaded_files", audioFile, audioFile.name);
            const res = await fetch(`${roboman_api}/campaign/upload_voice/`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData });
            if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
            const data = await res.json();

            let derivedVoiceId = data.voice_id;
            if (!derivedVoiceId && data.url) {
                const urlParts = data.url.split('/');
                derivedVoiceId = urlParts[urlParts.length - 1].replace('_voice', '');
            }
            const processedData = { ...data, voice_id: derivedVoiceId };

            setUploadData(processedData);
            setAudioFile(null);
            setIsStartingOver(false);
            localStorage.setItem('voiceUploadResponse', JSON.stringify(processedData));
            setStatus("✅ Upload successful!");
            setIsConfirmed(true);

            setTimeout(() => {
                onUploadSuccess?.(processedData.voice_id);
            }, 1000);

        } catch (err) {
            setStatus(`❌ Upload error: ${err.message}`);
            onUploadError?.(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleProceed = () => {
        if (uploadData) {
            setIsConfirmed(true);
            setTimeout(() => {
                onUploadSuccess?.(uploadData.voice_id);
            }, 1000);
        }
    };

    const renderContent = () => {
        // STATE 4: User has saved data and is NOT in the "start over" flow
        if (uploadData && !isStartingOver) {
            return (
                <div className="animate-fade-in">
                    <div className="w-full p-3  sm:p-4 mb-4 bg-violet-100 border border-violet-300 rounded-lg text-left text-sm sm:text-base text-blue-800">
                        <div className="flex items-center font-bold mb-2">
                             <Server className="w-4 h-4 mr-2" /> Previously Uploaded Voice 
                         </div>
                        <div className="pl-4 sm:pl-6 break-words space-y-1 text-xs sm:text-sm"> 
                            <p><strong>Voice :</strong> {uploadData.voice_id ? "voice uploaded" : 'Not found'}</p> 
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full items-stretch">
                        <ActionButton icon={<RefreshCcw className="w-5 h-5 text-white " />} label="Upload " onClick={handleStartOver} disabled={isConfirmed} />
                        <ActionButton icon={isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-6 h-6 text-white " />} label={isAudioReady ? (isPlaying ? "Pause" : "Play Preview") : "Loading..."} onClick={togglePlay} disabled={!isAudioReady || isConfirmed} variant="play" />
                        <button onClick={handleProceed} disabled={isConfirmed} className="flex items-center justify-center gap-2 sm:h-11 h-10 sm:px-4 px-3 w-full sm:w-auto rounded-lg text-sm sm:text-xs font-medium text-white shadow-sm hover:shadow-md  transition-all duration-200 bg-indigo-600 hover:bg-violet-600  disabled:bg-green-300 disabled:cursor-not-allowed">
                            {isConfirmed ? (
                                <>
                                    <Check className="w-5 h-5" />
                                    <span className="text-xs font-semibold tracking-wide uppercase">Selected</span>
                                </>
                            ) : (
                                <>
                                    <span className="text-xs font-semibold tracking-wide uppercase">Use This Voice</span>
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            );
        }

        // ✅ NEW STATE (STATE 0): An existing voice was found from the user's main profile
        if (hasExistingVoice) {
            return (
                <div className="animate-fade-in text-center">
                    <div className="w-full p-4 mb-6 bg-green-100 border border-green-300 rounded-lg text-green-800">
                        <div className="flex items-center justify-center font-bold">
                            <Check className="w-5 h-5 mr-2" /> Voice Note Exists
                        </div>
                    </div>
                    <div className="w-full max-w-xs mx-auto">
                        <ActionButton
                            icon={<RefreshCcw className="w-6 h-6 text-white" />}
                            label="Change Voice"
                            onClick={handleChangeVoice}
                        />
                    </div>
                </div>
            );
        }

        // STATE 2: User has a new file ready for preview
        if (audioFile) {
            return (
                <div className="w-full flex flex-col items-center animate-fade-in">
                    <div className="w-full bg-slate-100 p-3 sm:p-4 rounded-xl border border-slate-200 mb-4 sm:mb-6">
                        <h4 className="font-semibold text-slate-700 text-sm mb-2">Voice Preview</h4>
                        <div className="flex justify-center items-end gap-1 sm:gap-1.5 h-12 sm:h-16">
                            {[1, 2, 3, 4, 5, 6, 7].map((bar) => (<motion.div key={bar} className="w-1 sm:w-1.5 bg-gradient-to-t from-indigo-500 to-blue-500 rounded" animate={isPlaying ? { height: ["20%", "80%", "30%"] } : { height: "20%" }} transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut", delay: bar * 0.1 }} />))}
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch gap-3  w-full">
                        <ActionButton icon={<RefreshCcw className="w-5 h-5 sm:w-6 sm:h-6 text-white" />} label="Re-record" onClick={handleClearTemporary} disabled={loading} />
                        <ActionButton icon={isPlaying ? <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-white" /> : <Play className="w-6 h-6 text-white" />} label={isPlaying ? "Pause" : "Play Preview"} onClick={togglePlay} disabled={!isAudioReady || loading} variant="play" />
                        <ActionButton icon={loading ? <Loader className="animate-spin" /> : <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-white" />} label={loading ? "Uploading" : "Upload"} onClick={handleUpload} disabled={loading} variant="success" />
                    </div>
                </div>
            );
        }

        // STATE 1: Initial view (or "start over" view)
        return (
            <div className="animate-fade-in">
                <div className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-4">
                    <ActionButton icon={isRecording ? <Square className=" w-6 h-6 text-white" /> : <Mic className="w-5 h-5 text-white" />} label={isRecording ? "Stop" : "Record"} onClick={isRecording ? stopRecording : startRecording} disabled={loading} variant={isRecording ? 'danger' : 'primary'} />
                    <label className="cursor-pointer">
                        <div className="flex  items-center justify-center gap-2 h-10 sm:h-11  px-3 sm:px-4 rounded-lg text-sm font-medium text-white shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105 bg-green-600 hover:bg-green-700">
                            <Upload className=" w-5 h-5 text-white" /> <span className="text-xs font-semibold text-white tracking-wide uppercase">From File</span>
                        </div>
                        <input type="file" accept="audio/*" onChange={handleFileSelect} className="hidden" disabled={loading} />
                    </label>
                </div>
                {isStartingOver && (
                    <button onClick={handleCancelStartOver} className="mt-4 text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mx-auto">
                        <X size={14} /> Cancel and use saved voice
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="bg-slate-50/50 p-4 sm:p-6 rounded-2xl border border-slate-200 w-full max-w-4xl mx-auto">
            <p className="text-gray-600 mb-4 sm:mb-6 min-h-[20px] sm:min-h-[28px] font-medium text-sm sm:text-base">{status}</p>
            {renderContent()}
            <audio ref={audioRef} onEnded={() => setIsPlaying(false)} className="hidden" />
        </div>
    );
};

export default UploadVoiceStep;